import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail, ShieldAlert } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import axios from 'axios';
import { getPlatformVersion } from '../utils/platformDetection';

const Login = () => {
  const { login, check2FAStatus, get2FACode } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  const [discordId, setDiscordId] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loginBlocked, setLoginBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [blockType, setBlockType] = useState('');
  
  // Store interval references so we can clear them
  const [pollInterval, setPollInterval] = useState(null);
  const [codeUpdateInterval, setCodeUpdateInterval] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = useForm();

  // Check for login blocks on component mount
  useEffect(() => {
    const checkLoginBlock = async () => {
      try {
        const response = await axios.get('/api/login-blocks/check');
        if (response.data.success && response.data.data.isBlocked) {
          setLoginBlocked(true);
          setBlockMessage(response.data.data.message);
          setBlockType(response.data.data.blockType);
        }
      } catch (error) {
        console.error('Error checking login block:', error);
      }
    };
    checkLoginBlock();
  }, []);

  const clearAllIntervals = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
    if (codeUpdateInterval) {
      clearInterval(codeUpdateInterval);
      setCodeUpdateInterval(null);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const onSubmit = async (data) => {
    // Note: We don't block login attempts here because admins need to bypass blocks
    // The backend will handle the admin check and block non-admin users
    
    setIsLoading(true);
    // Clear any existing intervals from previous attempts
    clearAllIntervals();
    // Reset 2FA state on new login attempt
    setTwoFactorRequired(false);
    setTwoFactorMessage('');
    setDiscordId('');
    // Clear any previous errors
    setError('root', {});
    
    try {
      // Capture platform version for accurate Windows 10 vs 11 detection
      const platformVersion = await getPlatformVersion();
      console.log('[Login] Platform version captured:', platformVersion);
      
      // DEBUG: Show platform version to user
      if (platformVersion) {
        console.log('[Login] ✓ Platform version detected:', platformVersion);
      } else {
        console.log('[Login] ✗ Platform version NOT detected - API may not be available');
      }
      
      // Add platform version to login data
      const loginData = {
        ...data,
        platformVersion
      };
      console.log('[Login] Sending login data with platformVersion:', loginData);
      
      const result = await login(loginData);
      if (result.success && result.requiresTwoFactor) {
        // 2FA required - start polling
        setTwoFactorRequired(true);
        setTwoFactorMessage(result.message);
        setDiscordId(result.discord_id);
        startPolling(result.discord_id);
      } else if (!result.success) {
        setError('root', { message: result.message });
        setIsLoading(false);
      }
    } catch (error) {
      setError('root', { message: 'Login failed. Please try again.' });
      setIsLoading(false);
    }
  };

  const startPolling = (discord_id) => {
    // Poll for verification status every 2 seconds
    const newPollInterval = setInterval(async () => {
      const result = await check2FAStatus(discord_id);
      
      if (result.success && result.verified) {
        // Login successful
        clearAllIntervals();
        setIsLoading(false);
        // Navigation will happen automatically via AuthContext
      } else if (!result.requiresVerification) {
        // Error or expired - only show error if it's not the "waiting for verification" message
        if (result.message && !result.message.includes('verify') && !result.message.includes('Discord')) {
          clearAllIntervals();
          setIsLoading(false);
          setTwoFactorRequired(false);
          setError('root', { message: result.message });
        }
      }
      // If still requires verification, keep polling silently
    }, 2000); // Poll every 2 seconds
    setPollInterval(newPollInterval);

    // Poll for code updates every 30 seconds (backend regenerates on-demand)
    const newCodeUpdateInterval = setInterval(async () => {
      try {
        const response = await axios.post('/api/reseller-auth/get-current-2fa-code', { discord_id });
        if (response.data.success && response.data.message) {
          setTwoFactorMessage(response.data.message);
        }
      } catch (error) {
        console.error('Error fetching updated code:', error);
      }
    }, 30000); // Every 30 seconds
    setCodeUpdateInterval(newCodeUpdateInterval);

    // Stop polling after 5 minutes
    const newTimeoutId = setTimeout(() => {
      clearAllIntervals();
      setIsLoading(false);
      setTwoFactorRequired(false);
      setError('root', { message: '2FA verification timed out. Please try again.' });
    }, 300000);
    setTimeoutId(newTimeoutId);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#0a0a0a'}}>
      <div className="w-full max-w-sm">
        {/* Login Blocked Warning - Show when login is blocked */}
        {loginBlocked && (
          <div className="rounded-lg border p-6 mb-6" style={{
            backgroundColor: '#1a1a1a', 
            borderColor: blockType === 'emergency' ? '#ef4444' : blockType === 'security' ? '#f59e0b' : '#3b82f6'
          }}>
            <div className="flex">
              <div className="flex-shrink-0">
                <ShieldAlert className="h-6 w-6" style={{
                  color: blockType === 'emergency' ? '#ef4444' : blockType === 'security' ? '#f59e0b' : '#3b82f6'
                }} />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Login Temporarily Disabled
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {blockMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 2FA Verification Message - Show above form when required */}
        {twoFactorRequired && (
          <div className="rounded-lg border p-4 mb-6" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 animate-pulse" viewBox="0 0 20 20" fill="currentColor" style={{color: '#3b198e'}}>
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-200">
                      Discord Verification Required
                    </h3>
                    <div className="mt-2 text-sm text-gray-300">
                      <p className="font-mono p-2 rounded" style={{backgroundColor: '#0a0a0a'}}>{twoFactorMessage}</p>
                      <p className="mt-3 flex items-center">
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Waiting for Discord verification...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

        {/* Main Login Box with Glow Effect */}
        <div className="login-box-container relative">
          <div className="login-box rounded-2xl p-8 shadow-2xl transition-all duration-300 border" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
            {/* Welcome Text */}
            <h2 className="text-center text-3xl font-bold mb-8" style={{color: '#3b198e'}}>
              Welcome
            </h2>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Invalid email format'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className="block w-full rounded-lg py-2 pl-10 text-gray-100 placeholder-gray-400"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: errors.email ? '1px solid #ef4444' : '1px solid #4a4a4a',
                    outline: 'none',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    if (!errors.email) e.target.style.boxShadow = '0 0 0 2px #3b198e';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = '';
                  }}
                  placeholder="test@example.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 4,
                      message: 'Password must be at least 4 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="block w-full rounded-lg py-2 pl-10 pr-10 text-gray-100 placeholder-gray-400"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: errors.password ? '1px solid #ef4444' : '1px solid #4a4a4a',
                    outline: 'none',
                    transition: 'box-shadow 0.2s ease'
                  }}
                  onFocus={(e) => {
                    if (!errors.password) e.target.style.boxShadow = '0 0 0 2px #3b198e';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = '';
                  }}
                  placeholder="Secure"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-500 hover:text-gray-300" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-500 hover:text-gray-300" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              
              {/* Forgot Password Link */}
              <div className="mt-2">
                <button
                  type="button"
                  className="text-sm text-gray-400 hover:text-[#3b198e] transition-colors"
                  onClick={() => setShowForgotPasswordModal(true)}
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {/* Global Error */}
            {errors.root && !twoFactorRequired && (
              <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="text-sm text-red-700">{errors.root.message}</div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#3b198e] hover:bg-[#2d1270] hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3b198e] disabled:opacity-50 disabled:cursor-not-allowed transition-transform duration-200"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
            </form>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{backgroundColor: 'rgba(0, 0, 0, 0.75)'}}>
          <div className="rounded-lg border p-6 max-w-md w-full" style={{backgroundColor: '#1a1a1a', borderColor: '#4a4a4a'}}>
            <h3 className="text-xl font-bold mb-4" style={{color: '#3b198e'}}>Forgot Password?</h3>
            <p className="text-gray-300 mb-6">
              Support in the Discord can assist you reset any credentials you forgot.
            </p>
            <button
              onClick={() => setShowForgotPasswordModal(false)}
              className="w-full py-2 px-4 rounded-md text-white font-medium hover:scale-105 transition-transform duration-200"
              style={{backgroundColor: '#3b198e'}}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
